import { enviroment } from "../enviroment";
import { CartItem } from "../presentation/screens/order-create/models/CartItem";
import {
  deserializeCartFromStorage,
  isStorageQuotaError,
  serializeCartForStorage,
} from "./cartStorage";
import { ShadowStore } from "./cartShadowStore";

// Cart storage fallback (flag REACT_APP_CART_STORAGE_FALLBACK_ENABLED, design
// DESIGN-CART-STORAGE-TRIN3-2026-09-24). Step 3 of the cart storage work:
// with v2 on, a localStorage write can STILL throw QuotaExceeded, and until
// now the cart then lived in memory only (a reload lost every edit since the
// last successful write). This module is the React-free core CartContext
// calls from the v2 catch branches and from flag-gated additions only:
//
//   layer 1 - reclaimParkedBuckets: this user's own PARKED v1 buckets (other
//             act-as identities, `${b2CId}_<scope>_products`) are rewritten
//             to the slim v2 format once per session and the write is
//             retried. The typical act-as case ends here: no toast, no IDB.
//   layer 2 - writeShadow: a small marker `${bucket}_shadow` = `fp|nonce`
//             is written to localStorage SYNCHRONOUSLY (~35 chars fit when
//             a 45-200 KB payload did not), then the SAME v2 string is put
//             in IndexedDB under the bucket key, carrying the nonce. `fp` is
//             the fingerprint of what localStorage holds for the bucket in
//             the same tick, so a later load can decide "is the shadow
//             newer than the bucket" on localStorage alone.
//   load    - startShadowLoad: after the synchronous localStorage read, the
//             marker is read (the ONE extra getItem under the flag). No
//             marker -> nothing else happens (no IDB call, no database).
//             Marker with a matching fingerprint -> the record is fetched
//             and applied iff its nonce matches, the identity did not move
//             and the user made no structural change meanwhile.
//
// Module state (pendingLoads / deferred / bucketGen) survives a provider
// unmount on purpose (R6: ProfileContext's gate remounts the provider on
// act-as with a cold profile cache). Never toasts itself: the provider's
// toastOnce is injected through the host.

export const cartStorageFallbackOn: boolean =
  enviroment.cartStorageV2Enabled && enviroment.cartStorageFallbackEnabled;

export const SHADOW_MARKER_SUFFIX = "_products_shadow";

export function shadowMarkerKey(productsKey: string): string {
  return `${productsKey}_shadow`;
}

export const OWN_PARKED_BUCKET = (b2CId: string): RegExp =>
  new RegExp(`^${b2CId}_\\d+_products$`);

export const OWN_SHADOW_MARKER = (b2CId: string): RegExp =>
  new RegExp(`^${b2CId}_\\d+${SHADOW_MARKER_SUFFIX}$`);

// `${length}:${djb2 hex}`; null and "" fingerprint alike (a missing bucket
// and an empty one are the same "nothing" to a shadow).
export function fingerprintBucket(raw: string | null | undefined): string {
  const s = raw ?? "";
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0;
  }
  return `${s.length}:${(hash >>> 0).toString(16)}`;
}

export interface ShadowMarker {
  fp: string;
  nonce: string;
}

export function formatMarker(marker: ShadowMarker): string {
  return `${marker.fp}|${marker.nonce}`;
}

export function parseMarker(raw: string | null | undefined): ShadowMarker | null {
  if (!raw) return null;
  const parts = raw.split("|");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return { fp: parts[0], nonce: parts[1] };
}

// Unique across tabs in practice: a time part plus a random part.
export function newNonce(): string {
  return `${Date.now().toString(36)}.${Math.random().toString(36).slice(2, 8)}`;
}

const MIN_RECLAIM_CHARS = 2048;

export interface ReclaimResult {
  buckets: number;
  rewritten: number;
  charsBefore: number;
  charsAfter: number;
}

// Layer 1. Rewrites this user's own parked buckets (never the live key,
// never other users', never markers) from v1 to the slim v2 format - the
// very rule the next write in that identity would apply. Snapshot of the
// keys first (a write may reorder Storage.key(i)); a shrinking setItem does
// not throw in practice, and if it does the bucket is simply left as it was.
// Corrupt or empty buckets are never touched.
export function reclaimParkedBuckets(
  storage: Storage,
  b2CId: string,
  liveKey: string,
): ReclaimResult {
  const result: ReclaimResult = {
    buckets: 0,
    rewritten: 0,
    charsBefore: 0,
    charsAfter: 0,
  };
  const own = OWN_PARKED_BUCKET(b2CId);
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && key !== liveKey && own.test(key)) keys.push(key);
  }
  keys.forEach((key) => {
    const raw = storage.getItem(key);
    if (!raw) return;
    result.buckets += 1;
    if (raw.length < MIN_RECLAIM_CHARS) return;
    const items = deserializeCartFromStorage(raw);
    if (items.length === 0) return;
    const slim = serializeCartForStorage(items);
    if (slim.length >= raw.length) return;
    try {
      storage.setItem(key, slim);
    } catch {
      return;
    }
    result.rewritten += 1;
    result.charsBefore += raw.length;
    result.charsAfter += slim.length;
  });
  return result;
}

// What the provider hands the core: its storage, the shadow store, and the
// refs that must be shared with the React side. All refs are stable objects,
// so an async continuation reads their CURRENT value.
export interface CartStorageFallbackHost {
  storage: Storage;
  store: ShadowStore;
  b2CId: string;
  // v2's: true from the first quota failure until the next successful write.
  quotaEpisodeRef: { current: boolean };
  // Layer 1 ran this session (reset per b2CId).
  reclaimedRef: { current: boolean };
  // A marker was written or seen this session: the only case in which a
  // successful write or a deletion touches the marker / the store.
  shadowMayExistRef: { current: boolean };
  // Counts structural cart changes (add/remove/clear) - a shadow is applied
  // only when it is unchanged since the load started.
  structuralGenRef: { current: number };
  mountedRef: { current: boolean };
  // The scope the live items belong to (the provider's itemsScopeRef).
  itemsScopeRef: { current: number };
  toastShownRef: { current: boolean };
  setItems: (items: CartItem[]) => void;
  // The ONE toast per episode (gated by toastShownRef in the provider).
  toastOnce: () => void;
}

export type PersistOutcome =
  | "not-quota"
  | "reclaimed"
  | "failed"
  | "deferred"
  | "shadow";

interface PendingLoad {
  token: number;
  nonce: string;
  genAtStart: number;
  scopeAtStart: number;
}

const pendingLoads = new Map<string, PendingLoad>();
const deferred = new Map<
  string,
  { payload: string; gen: number; toastAllowed: boolean }
>();
const bucketGen = new Map<string, number>();
let loadToken = 0;
let storeCleanupDone = false;

function getGen(key: string): number {
  return bucketGen.get(key) ?? 0;
}

function bumpGen(key: string): void {
  bucketGen.set(key, getGen(key) + 1);
}

function safeGetItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemoveItem(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Nothing to do - a marker that cannot be removed is dropped on load.
  }
}

// Removes the marker only while it still carries OUR nonce - a newer marker
// (a later failed persist) must survive an older put's failure.
function removeMarkerIfNonce(storage: Storage, key: string, nonce: string): void {
  const parsed = parseMarker(safeGetItem(storage, shadowMarkerKey(key)));
  if (parsed && parsed.nonce === nonce) {
    safeRemoveItem(storage, shadowMarkerKey(key));
  }
}

// S3: the mount echo-write must not invalidate the shadow - same nonce, new
// fingerprint (same size, so the write does not throw in practice).
function refreshMarkerFingerprint(storage: Storage, key: string, fp: string): void {
  const markerKey = shadowMarkerKey(key);
  const parsed = parseMarker(safeGetItem(storage, markerKey));
  if (!parsed || parsed.fp === fp) return;
  try {
    storage.setItem(markerKey, formatMarker({ fp, nonce: parsed.nonce }));
  } catch {
    // Left as it was: the next load drops it as stale (today's behaviour).
  }
}

function writeShadow(
  host: CartStorageFallbackHost,
  key: string,
  payload: string,
  toastAllowed: boolean,
): "shadow" | "failed" {
  // localStorage is unchanged since the setItem that threw (same tick).
  const fp = fingerprintBucket(safeGetItem(host.storage, key));
  const nonce = newNonce();
  try {
    host.storage.setItem(shadowMarkerKey(key), formatMarker({ fp, nonce }));
  } catch {
    // R16: not even ~35 chars fit - nothing is saved, say so.
    if (toastAllowed) host.toastOnce();
    return "failed";
  }
  host.shadowMayExistRef.current = true;
  host.store
    .put({ key, payload, nonce, writtenAt: Date.now() })
    .then(() => {
      console.info(
        "[cart-storage] localStorage full - cart saved to IndexedDB shadow (%d chars, %s)",
        payload.length,
        key,
      );
    })
    .catch((error) => {
      removeMarkerIfNonce(host.storage, key, nonce);
      console.warn("[cart-storage] shadow write failed for %s", key, error);
      if (toastAllowed) host.toastOnce();
    });
  return "shadow";
}

// Called from a v2 catch branch after the write of `payload` to `key` threw
// `error`. Layer 1 then layer 2; `toastAllowed` is false for the park and
// the unmount flush (they never toasted, and still do not).
export function persistWithFallback(
  host: CartStorageFallbackHost,
  key: string,
  payload: string,
  error: unknown,
  toastAllowed: boolean,
): PersistOutcome {
  if (!isStorageQuotaError(error)) return "not-quota";
  const episodeStart = !host.quotaEpisodeRef.current;
  host.quotaEpisodeRef.current = true;
  if (episodeStart) host.store.allowRetryAfterTimeout();

  if (!host.reclaimedRef.current) {
    host.reclaimedRef.current = true;
    const reclaimed = reclaimParkedBuckets(host.storage, host.b2CId, key);
    if (reclaimed.rewritten > 0) {
      try {
        host.storage.setItem(key, payload);
        host.quotaEpisodeRef.current = false;
        console.debug(
          "[cart-storage] wrote %d chars to %s after reclaiming %d chars from %d parked buckets",
          payload.length,
          key,
          reclaimed.charsBefore - reclaimed.charsAfter,
          reclaimed.rewritten,
        );
        onLsWriteSuccess(host, key, payload);
        return "reclaimed";
      } catch {
        // Still full: layer 2.
      }
    } else {
      console.debug(
        "[cart-storage] reclaim freed 0 chars (%d parked buckets already v2)",
        reclaimed.buckets,
      );
    }
  }

  if (host.store.unavailable) {
    if (toastAllowed) host.toastOnce();
    return "failed";
  }
  if (pendingLoads.has(key)) {
    // R2: never write over a shadow that is being read - the payload waits
    // for the load's verdict (dropped if the shadow is applied).
    deferred.set(key, { payload, gen: getGen(key), toastAllowed });
    console.info("[cart-storage] shadow write deferred (load pending) for %s", key);
    return "deferred";
  }
  return writeShadow(host, key, payload, toastAllowed);
}

// Called after a SUCCESSFUL localStorage write of `payload` to `key` (persist,
// park, unmount flush). A shadow is superseded - unless this is the mount
// echo-write while that shadow is still being loaded (S3): then the marker's
// fingerprint follows the bucket and the nonce is kept.
export function onLsWriteSuccess(
  host: CartStorageFallbackHost,
  key: string,
  payload: string,
): void {
  host.toastShownRef.current = false;
  bumpGen(key);
  const load = pendingLoads.get(key);
  if (load && host.structuralGenRef.current === load.genAtStart) {
    refreshMarkerFingerprint(host.storage, key, fingerprintBucket(payload));
    return;
  }
  if (!host.shadowMayExistRef.current) return;
  safeRemoveItem(host.storage, shadowMarkerKey(key));
  host.store.delete(key).catch(() => undefined);
  host.shadowMayExistRef.current = false;
}

// Every deletion of a bucket: the marker goes SYNCHRONOUSLY with it (the
// primary guard against a revived cart, Stripe included), the record is
// fire-and-forget hygiene, and a deferred put planned before the clear is
// dropped through bucketGen.
export function clearBucketShadow(host: CartStorageFallbackHost, key: string): void {
  safeRemoveItem(host.storage, shadowMarkerKey(key));
  bumpGen(key);
  deferred.delete(key);
  if (!host.shadowMayExistRef.current) return;
  host.store.delete(key).catch(() => undefined);
}

// Identity switch: a load for the outgoing key is cancelled (its result
// would land on the incoming identity's items) and so is a deferred payload
// for it - the park that follows writes the outgoing bucket directly.
export function cancelShadowLoad(key: string): void {
  pendingLoads.delete(key);
  deferred.delete(key);
}

// Once per page, and only when the database is being opened anyway: drop
// records of other users (shared machine - same privacy level as the
// localStorage cleanup at mount) and own records without a marker
// (orphans after a Stripe clear). Fire-and-forget.
function cleanupShadowStore(host: CartStorageFallbackHost): void {
  if (storeCleanupDone) return;
  storeCleanupDone = true;
  const own = OWN_PARKED_BUCKET(host.b2CId);
  host.store
    .keys()
    .then((keys) => {
      keys.forEach((key) => {
        if (own.test(key) && safeGetItem(host.storage, shadowMarkerKey(key)) != null) {
          return;
        }
        host.store.delete(key).catch(() => undefined);
      });
    })
    .catch((error) => {
      console.warn("[cart-storage] shadow store cleanup skipped", error);
    });
}

// Mount / identity switch, AFTER the synchronous localStorage read that gave
// `rawAtStart`. See the module comment for the gate.
export function startShadowLoad(
  host: CartStorageFallbackHost,
  key: string,
  rawAtStart: string | null,
): void {
  const markerKey = shadowMarkerKey(key);
  const marker = safeGetItem(host.storage, markerKey);
  if (marker == null) return;
  host.shadowMayExistRef.current = true;
  const parsed = parseMarker(marker);
  if (!parsed) {
    safeRemoveItem(host.storage, markerKey);
    return;
  }
  if (parsed.fp !== fingerprintBucket(rawAtStart)) {
    // R3/R9: the bucket changed after the shadow was written (a later
    // successful write, another tab) - decided on localStorage alone.
    safeRemoveItem(host.storage, markerKey);
    console.info("[cart-storage] stale shadow dropped for %s", key);
    return;
  }
  if (host.store.unavailable) {
    console.warn(
      "[cart-storage] IndexedDB shadow unavailable - shadow for %s not loaded",
      key,
    );
    return;
  }
  const token = ++loadToken;
  const genAtStart = host.structuralGenRef.current;
  const scopeAtStart = host.itemsScopeRef.current;
  pendingLoads.set(key, { token, nonce: parsed.nonce, genAtStart, scopeAtStart });
  let applied = false;
  host.store
    .get(key)
    .then((record) => {
      if (pendingLoads.get(key)?.token !== token || !host.mountedRef.current) return;
      if (host.itemsScopeRef.current !== scopeAtStart) return;
      if (!record || record.nonce !== parsed.nonce) {
        // S4: a marker without ITS record (put not committed before the
        // reload, or an orphan of an earlier episode) - never an old record
        // under a new marker.
        removeMarkerIfNonce(host.storage, key, parsed.nonce);
        console.info("[cart-storage] shadow missing for %s", key);
        return;
      }
      if (host.structuralGenRef.current !== genAtStart) {
        // R4: the user changed the cart while the shadow was loading - the
        // user's latest state wins and the next persist overtakes the shadow.
        console.warn(
          "[cart-storage] shadow for %s not applied: cart changed during load",
          key,
        );
        return;
      }
      applied = true;
      host.setItems(deserializeCartFromStorage(record.payload));
      console.info(
        "[cart-storage] restored %d chars for %s from IndexedDB shadow",
        record.payload.length,
        key,
      );
    })
    .catch((error) => {
      // Timeout / unavailable: the marker stays (a later session may read
      // it); today's behaviour for now.
      console.warn("[cart-storage] shadow load failed for %s", key, error);
    })
    .finally(() => {
      if (pendingLoads.get(key)?.token !== token) return;
      pendingLoads.delete(key);
      const waiting = deferred.get(key);
      deferred.delete(key);
      if (waiting && !applied && waiting.gen === getGen(key)) {
        // The user's latest state becomes the shadow.
        writeShadow(host, key, waiting.payload, waiting.toastAllowed);
      }
    });
  cleanupShadowStore(host);
}

export function hasPendingShadowLoadForTests(key: string): boolean {
  return pendingLoads.has(key);
}

export function resetCartStorageFallbackForTests(): void {
  pendingLoads.clear();
  deferred.clear();
  bucketGen.clear();
  loadToken = 0;
  storeCleanupDone = false;
}
